import React, {ReactNode, useEffect, useLayoutEffect, useRef, useState} from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import {CrewCandidateDto} from '../../job/dtos/company-crew-member.dto';
import {EstimateCrewMemberDto} from '../../../shared/dtos/estimate-crew-member.dto';
import {formatMinutes} from './record-math';

/*
 * The Time & Price Calculator (EstimateSectionView.kt TimeAndPriceCalculator):
 * the crew the time is worked out for, as initials in a bar, and the job's
 * estimated time. Tapping the bar opens the roster inline; a tap anywhere
 * else closes it. The crew can be changed in view mode too, as on Android.
 */

export interface RosterMember {
  id: number;
  name: string;
  /** Has no billable rate on their profile: shown dimmed, and the tap explains instead of picking. */
  noRate: boolean;
}

/** The picker offers team members; a legacy crew-list member already on the estimate joins from its own row. */
export const rosterOf = (candidates: CrewCandidateDto[], crew: EstimateCrewMemberDto[]): RosterMember[] => [
  ...candidates.map(c => ({id: c.companyCrewMemberId, name: c.name, noRate: !c.selectable})),
  ...crew.filter(m => !candidates.some(c => c.companyCrewMemberId === m.companyCrewMemberId))
    .map(m => ({id: m.companyCrewMemberId, name: m.name, noRate: false})),
];

export const crewInitials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

const Avatar = ({name, selected = true, size = 28}: {name: string; selected?: boolean; size?: number}) => (
  <span className={selected ? 'er-avatar' : 'er-avatar er-avatar--off'} style={{width: size, height: size, fontSize: size < 40 ? 11 : 13}}>
    {crewInitials(name)}
  </span>
);

/** RateChangeNotice: the roster moved on since a member was added; their time still prices at the rate locked then. */
const hourly = (n: number) => `$${n.toLocaleString('en-US', {minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2})}/hr`;
const rateNotice = (m: EstimateCrewMemberDto) => {
  if (!m.rateChanged) return undefined;
  const name = m.name.trim() || 'This crew member';
  return m.hourlyRate != null && m.currentHourlyRate != null
    ? `${name}'s rate changed to ${hourly(m.currentHourlyRate)}. This estimate keeps ${hourly(m.hourlyRate)}; remove and add them again to use the new rate.`
    : `${name}'s billable rate has changed since they were added. This estimate keeps the earlier rate; remove and add them again to use the new one.`;
};

/** The roster to pick from: tap a member to add or remove them (no rate: dimmed, and the tap explains). */
const RosterGrid = ({roster, selectedIds, onToggle}: {roster: RosterMember[]; selectedIds: number[]; onToggle: (id: number) => void}) => (
  roster.length === 0
    ? <div className="er-small er-muted er-crew-empty">No crew members on the company roster yet</div>
    : (
      <div className="er-roster">
        {roster.map(m => (
          <button type="button" key={m.id} className={m.noRate ? 'er-roster-member er-roster-member--dim' : 'er-roster-member'}
                  onClick={() => onToggle(m.id)} aria-pressed={selectedIds.includes(m.id)}>
            <Avatar name={m.name} selected={selectedIds.includes(m.id)} size={44}/>
            <span className="er-roster-name">{m.name}</span>
            {m.noRate && <span className="er-roster-norate">No rate set</span>}
          </button>
        ))}
      </div>
    )
);

/**
 * SQ-660 (CrewPickerSheet): the crew picker as a dialog, opened from a timer
 * that has no crew. Several can be picked; Done or a click outside closes it.
 * The crew then shows in the card at the top, as if picked there.
 */
export const CrewPickerDialog = ({roster, selectedIds, onToggle, onClose}: {
  roster: RosterMember[]; selectedIds: number[]; onToggle: (id: number) => void; onClose: () => void;
}) => {
  // Centred over the record column, not the window: with the map open beside
  // the record, the window's centre is over the map.
  const anchor = useRef<HTMLSpanElement>(null);
  const [column, setColumn] = useState<{left: number; width: number}>();
  useLayoutEffect(() => {
    const measure = () => {
      const rect = anchor.current?.closest('.rk-record')?.getBoundingClientRect();
      setColumn(rect ? {left: rect.left, width: rect.width} : undefined);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return <>
    <span ref={anchor} hidden/>
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth
            sx={column ? {'& .MuiDialog-container': {marginLeft: `${column.left}px`, width: `${column.width}px`}} : undefined}>
      <DialogTitle>Select crew members</DialogTitle>
      <DialogContent>
        <div className="er-small er-muted er-crew-dialog-lead">Timers work out time and price from the crew's rates.</div>
        <RosterGrid roster={roster} selectedIds={selectedIds} onToggle={onToggle}/>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  </>;
};

const CrewCard = ({roster, selectedIds, crewRows, jobMinutes, timeHidden = false, canChange, onToggle, jobTime}: {
  roster: RosterMember[];
  selectedIds: number[];
  crewRows: EstimateCrewMemberDto[];
  /** Total Est. Job Time; undefined with no crew. */
  jobMinutes?: number;
  /** SQ-636: the record's estimated time was hidden from this person (pricesMasked): no Total Est. Job Time, nor the heading about it. */
  timeHidden?: boolean;
  canChange: boolean;
  onToggle: (id: number) => void;
  /** A job (SQ-597): JOB TIME and the crew's clocks, under the picker. */
  jobTime?: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const selected = roster.filter(m => selectedIds.includes(m.id));
  const notices = crewRows.filter(m => selectedIds.includes(m.companyCrewMemberId)).map(rateNotice).filter(Boolean);
  return (
    <div className="er-info er-crew" ref={ref}>
      {!timeHidden && <div className="er-crew-lead">Estimating time based on selected crew members</div>}
      <div className="er-crew-row">
        <span className="er-crew-label">Select crew members</span>
        {!timeHidden && (
          <span className="er-crew-pill">
            <span className="er-crew-pill-caption">Total Est. Job Time</span>
            {jobMinutes !== undefined && <span className="er-crew-pill-time">{formatMinutes(jobMinutes)}</span>}
          </span>
        )}
      </div>
      <button type="button" className="er-crew-bar" disabled={!canChange} onClick={() => setOpen(o => !o)} aria-expanded={open}
              aria-label={selected.length ? `Crew: ${selected.map(m => m.name).join(', ')}` : 'No crew selected'}>
        {selected.length === 0
          ? <span className="er-muted er-crew-none">No crew selected</span>
          : <span className="er-crew-avatars">{selected.map(m => <Avatar key={m.id} name={m.name}/>)}</span>}
        {canChange && (open ? <KeyboardArrowUpIcon className="er-crew-chevron"/> : <KeyboardArrowDownIcon className="er-crew-chevron"/>)}
      </button>
      {notices.map(n => <div key={n} className="er-notice">{n}</div>)}
      {open && canChange && <RosterGrid roster={roster} selectedIds={selectedIds} onToggle={onToggle}/>}
      {jobTime}
    </div>
  );
};

export default CrewCard;
